import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject, concatMap, tap } from 'rxjs';
import { RolesPermissionsFactory } from './roles-permissions.factory';
import { Role, RoleData, RolePermissionLabel, Permission, RemoveRoleModalState, RolesPermissionsPayload, PermissionData } from './definitions';
import { ApiService } from '../../services/api/api.service';
import { LoggerService } from '../../services/logger/logger.service';
import { ToastrService } from 'ngx-toastr';
import { ModalState } from 'kinetic-angular';
import { HttpErrorResponse } from '@angular/common/http';
import { ApiResponse } from '../../definitions/api-response.interface';
import { AnalyticsService } from '../../services/analytics/analytics.service';
import { SharedService } from '../../services/shared/shared.service';
import { ShellService } from '../shell/shell.service';
import { RoutePermissions } from '../../guards/permission/definitions';
import { AuthenticationService } from '../../services/authentication/authentication.service';
import { LocalStorageService } from '../../services/local-storage/local-storage.service';
import { ButtonPosition, ButtonSize, ButtonStyle, CssClass, Icons, PermissionCategory, PermissionDefault } from '../../services/shared/shared-enums';
import { individualUserRoleId, mandatoryPermissionIds, removePermissionIdsforCustomRole } from '../../services/shared/shared-constants';
import { SharedUtilService } from '../../services/shared/shared-util.service';
@Injectable({
	providedIn: 'root'
})
export class RolesPermissionsService
{
	private readonly selectedRoleIndex$: BehaviorSubject<number> = new BehaviorSubject<number>(0);
	private readonly roleToBeRemoved: BehaviorSubject<Role | null> = new BehaviorSubject<Role | null>(null);
	private readonly addRowActive$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	private readonly copyRoleActive$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	private readonly contentUpdated$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	private selectedRoleId: string = '';
	addButtonState = this.sharedUtilService.getButtonState(ButtonSize.medium, ButtonStyle.linkPrimary, '', false, { altText: '', iconPath: '' }, ButtonPosition.left);
	saveButtonState = this.sharedUtilService.getButtonState(ButtonSize.medium, ButtonStyle.primary, '', false, { altText: '', iconPath: '' }, ButtonPosition.left);
	isDirty: boolean = false;
	isSaveSucess$: Subject<boolean> = new Subject<boolean>();
	rolesList: Role[] = [];
	permissionsList: Permission[] = [];
	labels: RolePermissionLabel = RolesPermissionsFactory.buildAEMLabels();
	roleNameList: string[] = [];
	removeRoleModalState: RemoveRoleModalState | null = null;
	countOfUserstoRemove: number = 0;
	roleDetailsLoader: boolean = false;
	removeModalState: ModalState = {
		hidden: true,
		label: this.labels.deleteConfirmation[0].headingMain,
		buttons: [
			{ label: '', disabled: false, cssClass: CssClass.btnSecondary },
			{ label: this.labels.deleteConfirmation[0].removeRole, disabled: false, cssClass: CssClass.btnDanger, iconPath: Icons.trashIcon }
		],
		cssClass: ''
	};

	get selectedRoleIndex(): number
	{
		return this.selectedRoleIndex$.value;
	}
	get roleToRemove(): Role | null
	{
		return this.roleToBeRemoved.value;
	}
	get addRowActive(): boolean
	{
		return this.addRowActive$.value;
	}
	get copyRoleActive(): boolean
	{
		return this.copyRoleActive$.value;
	}
	get contentUpdated(): boolean
	{
		return this.contentUpdated$.value;
	}
	get currentUserRole(): string
	{
		const userProfile = this.localStorageService.getLoggedInUser();
		return userProfile ? this.localStorageService.parseJSON(userProfile).roles[0] : '';
	}
	get contentUpdatedObservable$(): Observable<boolean>
	{
		return this.contentUpdated$;
	}
	set updatedContent(value: boolean)
	{
		this.contentUpdated$.next(value);
	}

	constructor(public apiService: ApiService, private loggerService: LoggerService, private toastr: ToastrService, private analyticsService: AnalyticsService,
		private sharedService: SharedService, private shellService: ShellService, private authenticationService: AuthenticationService, private localStorageService: LocalStorageService, private sharedUtilService: SharedUtilService)
	{
		this.rolesList = [];
	}

	public trackSaveRoleAnalyticsEvent()
	{
		this.analyticsService.trackByEvent({ event: 'save changes', link: { name: 'changes:save changes' } });
	}
	public trackLeaveUnsavedChangesAnalyticsEvent()
	{
		this.analyticsService.trackByEvent({ event: 'discard changes', link: { name: 'changes:leave page' } });
	}
	public trackStayOnPageAnalyticsEvent()
	{
		this.analyticsService.trackByEvent({ event: 'unsaved changes warning', link: { name: 'changes:stay on page' } });
	}
	public initAnalyticsData(): void
	{
		this.analyticsService.trackByEvent({ event: 'pageView', page: { name: 'roles and permissions' } });
	}
	public updateSelectedRoleIndex(value: number): void
	{
		this.selectedRoleIndex$.next(value);
	}
	public updateRoleToBeRemoved(role: Role | null): void
	{
		this.roleToBeRemoved.next(role);
	}
	public updateAddRowActiveStatus(value: boolean): void
	{
		this.addRowActive$.next(value);
	}
	public updateCopyRoleActiveStatus(value: boolean): void
	{
		this.copyRoleActive$.next(value);
	}
	updateExistingRoleNameList()
	{
		this.roleNameList = [];
		if (this.rolesList !== undefined)
			this.rolesList.forEach((role) => this.roleNameList.push(role.name));
	}
	public getRolesPermissionsData(): void
	{
		const payload: RolesPermissionsPayload = { company: '' };
		this.roleDetailsLoader = false;
		this.sharedService.getLoggedInUserCompanyObs().pipe(tap((companyId: string) =>
		{
			payload.company = companyId;
		}), concatMap(() => this.apiService.getRolesPermissionsData(payload))).subscribe({
			next: (res: ApiResponse) =>
			{
				if (res?.data)
				{
					const responseObject = <RoleData>res.data;
					this.rolesList = responseObject.rolesData;

					const indexOfIndividualRole = this.rolesList.findIndex(role => role.id === individualUserRoleId);
					if (indexOfIndividualRole !== -1)
						this.rolesList.splice(indexOfIndividualRole, 1);
					this.selectCurrentRole([this.currentUserRole]);
					this.sharedService.getLoggedInUserRole().subscribe((roles: string[]) =>
					{
						this.selectCurrentRole(roles);
					});
					this.labels = responseObject.aemPageLabels;
					this.labels.unselectAllText = this.labels.unSelectAllLabel;
					this.labels.customRoleDefaultDescription = 'This is a custom role, view Roles and Permissions for details.';
					this.labels.permissionLabels.forEach(label =>
					{
						if (!label.permissionArr)
							label.permissionArr = [];
						else
						{
							label.permissionArr.forEach((permission: PermissionData) =>
							{
								const arr = permission.permissionDescription ? permission.permissionDescription.split('. ') : [];
								permission.permissionDescription = arr.join('. <br/>');
							});
						}
					});
					this.labels.customRoleDefaultName = 'New Role Name';
					this.labels.duplicateRoleNameErrorText = this.labels.duplicateRoleErrorText;
					this.addButtonState.label = this.labels.addRole;
					this.saveButtonState.label = this.labels.saveChangesBtnText;
					this.shellService.stateObservable$.subscribe(res => 
					{
						if (!res.isMyInSights && res.isMyInSights !== undefined) 
						{
							this.filterPermissionsForMyInsights();
						}
					});
					this.permissionsList = this.labels.permissionLabels;
					this.addButtonState.icon.iconPath = Icons.addLink;
					this.updateExistingRoleNameList();
					this.updatedContent = true;
				}
			},
			error: (error: HttpErrorResponse) =>
			{
				this.roleDetailsLoader = true;
				this.loggerService.log(error.error.message);
				this.toastr.error(error.error.message);
				this.updatedContent = true;
			},
			complete: () =>
			{
				setTimeout(() =>
				{
					this.roleDetailsLoader = true;
					this.updatedContent = true;
				}, 1000);
			}
		});
	}
	private filterPermissionsForMyInsights() 
	{
		this.labels.permissionLabels.forEach(permission => 
		{
			if (permission.sectionHeader === 'MyInsights') 
			{
				permission.permissionArr = permission.permissionArr.filter(this.isViewMyInsightsMain);
			}
		});
	}
	private isViewMyInsightsMain(permission: PermissionData) 
	{
		return permission.permissionKey === 'VIEW_MYINSIGHTS_MAIN';
	}
	selectCurrentRole(roles: string[])
	{
		if (roles)
			roles.forEach((role: string) => this.selectedRoleId = role);
		if (this.selectedRoleId)
			this.updateSelectedRoleIndex(this.rolesList?.findIndex((role: Role) => role.id === this.selectedRoleId));
	}
	public addCustomRole(): void
	{
		const newRole = RolesPermissionsFactory.buildAddRolelist();
		newRole.name = this.labels.customRoleDefaultName;
		newRole.description = this.labels.customRoleDefaultDescription;
		this.rolesList.push(newRole);
		this.updateSelectedRoleIndex(this.rolesList.length - 1);
	}
	public copyCustomRole(roleIndex: number): void
	{
		const copiedRole: Role = JSON.parse(JSON.stringify(this.rolesList[roleIndex]));
		copiedRole.id = '';
		copiedRole.name = ('Copy of ' + copiedRole.name).slice(0, 50);
		copiedRole.description = this.labels.customRoleDefaultDescription;
		copiedRole.locked = false;
		copiedRole.action = 'Copy';
		this.rolesList.push(copiedRole);
		this.updateSelectedRoleIndex(this.rolesList.length - 1);
	}
	public saveCustomRole(role: Role): void
	{
		const roleToAdd: Role = role;
		delete roleToAdd.id;
		delete roleToAdd.isInvalidText;
		const actionPerformed = roleToAdd.action;
		delete roleToAdd.action;
		role.permissions = this.updateMandatoryPermissions(role.permissions);
		this.sharedService.getLoggedInUserCompanyObs().pipe(tap((companyId: string) =>
		{
			roleToAdd.company = companyId;
		}), concatMap(() => this.apiService.saveCustomRole(roleToAdd))).subscribe({
			next: (data: ApiResponse) =>
			{
				if (data?.data)
				{
					this.isSaveSucess$.next(false);
					const responseObject: Role = <Role>data.data;
					this.toastr.success(responseObject?.name + ' successfully saved.');
					this.analyticsService.trackByEvent({ event: 'role added', link: { name: 'role:role added' }, role: { roleAdded: responseObject?.name } });
					const indexOfRoleToAdd = this.rolesList.findIndex((el: Role) => el.name === role.name);

					if (actionPerformed === 'Add')
						this.addButtonState.disabled = false;
					else if (actionPerformed === 'Copy')
						this.updateCopyRoleActiveStatus(false);

					this.rolesList[indexOfRoleToAdd].id = responseObject?.id;
					delete this.rolesList[indexOfRoleToAdd].action;
					this.updateExistingRoleNameList();
				}
			},
			error: (error: HttpErrorResponse): void =>
			{
				this.isSaveSucess$.next(true);
				this.loggerService.log(error.error.message);
				this.toastr.error(error.error.message);
			}
		});
	}
	public updateCustomRole(role: Role)
	{
		const roleToUpdate: Role = role;
		delete roleToUpdate.company;
		delete roleToUpdate.locked;
		delete roleToUpdate.isInvalidText;
		role.permissions = this.updateMandatoryPermissions(role.permissions);
		this.apiService.updateCustomRole(roleToUpdate).subscribe({
			next: (res: ApiResponse) =>
			{
				if (res?.data)
				{
					this.isSaveSucess$.next(false);
					this.loggerService.log('updated res' + res);
					this.toastr.success(roleToUpdate.name + ' has been updated.');
					this.updateExistingRoleNameList();
				}
			},
			error: (error: HttpErrorResponse) =>
			{
				this.isSaveSucess$.next(true);
				this.loggerService.log(error.error.message);
				this.toastr.error(error.error.message);
			}
		});
	}
	public removeRole(id: string)
	{
		const indexOfRoleToBeRemoved = this.rolesList.findIndex((el) => el.id === id);
		this.apiService.removeRole(id).subscribe({
			next: (res: ApiResponse) =>
			{
				if (res?.data)
				{
					const responseObject: RemoveRoleModalState = <RemoveRoleModalState>(res.data);
					this.loggerService.log(JSON.stringify(responseObject));
					this.toastr.success(this.rolesList[indexOfRoleToBeRemoved].name + ' successfully removed.');
					this.analyticsService.trackByEvent({
						event: 'role removed',
						link: { name: 'role:role removed' },
						role: { roleRemoved: this.rolesList[indexOfRoleToBeRemoved].name }
					});
					this.rolesList.splice(indexOfRoleToBeRemoved, 1);
					if (this.selectedRoleIndex >= this.rolesList.length)
						this.updateSelectedRoleIndex(this.rolesList.length - 1);
					this.updateExistingRoleNameList();
					this.removeRoleModalState = null;
					this.updateRoleToBeRemoved(null);
					this.removeModalState.hidden = true;
				}
			},
			error: (error: HttpErrorResponse) =>
			{
				this.loggerService.log(error.error.message);
				this.toastr.error(error.error.message);
				this.removeRoleModalState = null;
				this.updateRoleToBeRemoved(null);
				this.removeModalState.hidden = true;
			}
		});
	}
	public getRemoveRoleState(id: string): void
	{
		this.apiService.getRemoveRoleState(id).subscribe({
			next: (res: ApiResponse) =>
			{
				if (res?.data)
				{
					const responseObject: RemoveRoleModalState = <RemoveRoleModalState>(res.data);
					if (responseObject.userCount > 0)
					{
						this.removeRoleModalState = responseObject;
						this.removeModalState.label = this.labels.deleteConfirmation[0].headingMain;
						this.removeRoleModalState.manageUserText = this.labels.deleteConfirmation[0].manageLabel;
						this.removeModalState.buttons[0].label = this.labels.deleteConfirmation[0].cancel;
						this.removeModalState.buttons[1].label = this.labels.deleteConfirmation[0].removeRole;
						responseObject.users = this.labels.deleteConfirmation[0].users;
					}
					else
						this.removeRole(id);
				}
			},
			error: (error: HttpErrorResponse) =>
			{
				this.loggerService.log(error.error.message);
				this.toastr.error(error.error.message);
			}
		});
	}
	public updateShell(value: boolean = true): void
	{
		this.shellService.updateActionFooterPresent(value);
	}
	public getPermissions(): RoutePermissions | undefined
	{
		return this.authenticationService.getFetchedPermission({ category: PermissionCategory.rolesPermissions, action: PermissionDefault.viewDetails });
	}
	updateMandatoryPermissions(permissions: string[])
	{
		permissions = permissions.filter(permission => !removePermissionIdsforCustomRole.includes(permission));
		mandatoryPermissionIds.forEach(permission =>
		{
			if (!permissions.includes(permission))
				permissions.push(permission);
		});
		return permissions;
	}
}
